import React from 'react';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import AnnouncementBanner from '@/components/AnnouncementBanner';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { Button } from '@/components/ui/button';
import { Eye, ShieldAlert } from 'lucide-react';
import { DeployUpdatePill } from '@/components/DeployUpdateNotice';
import { rememberSidebarChoice, sidebarStartsOpen } from '@/lib/sidebarStart';

interface AppShellProps {
  sidebar: React.ReactNode;
  /** Optional content rendered in the sticky header next to the sidebar
   * trigger — used for page-context navigation like "Back to Dashboard". */
  headerSlot?: React.ReactNode;
  children: React.ReactNode;
}

export default function AppShell({ sidebar, headerSlot, children }: AppShellProps) {
  const { isImpersonating, impersonatedProfile, stopImpersonation } = useImpersonation();
  const [sidebarOpen, setSidebarOpen] = React.useState(() => sidebarStartsOpen());
  const onSidebarChange = (open: boolean) => {
    rememberSidebarChoice(open);
    setSidebarOpen(open);
  };

  return (
    <SidebarProvider open={sidebarOpen} onOpenChange={onSidebarChange}>
      <div className="min-h-screen flex w-full">
        {sidebar}
        <div className="flex-1 flex flex-col min-h-screen min-w-0">
          {/* Sandbox impersonation banner — sticky, high-visibility */}
          {isImpersonating && (
            <div className="bg-amber-600 text-white px-4 py-2 flex items-center gap-3 text-sm font-medium sticky top-0 z-[60]">
              <ShieldAlert className="h-4 w-4 shrink-0 animate-pulse" />
              <span className="flex-1">
                <strong>Sandbox Mode</strong> — Viewing as <strong>{impersonatedProfile?.full_name || impersonatedProfile?.email}</strong> — changes will not be saved
              </span>
              <Button size="sm" variant="secondary" className="h-7 text-xs" onClick={stopImpersonation}>
                Exit Sandbox
              </Button>
            </div>
          )}

          {/* System announcement banner */}
          <AnnouncementBanner />

          <header className="h-12 flex items-center gap-3 border-b bg-card/80 backdrop-blur-sm sticky top-0 z-50 px-4">
            <SidebarTrigger />
            {headerSlot}
            {/* Right edge of the strip: only visible after a deploy lands
                while this tab is open, and only for someone who hasn't
                navigated since. ml-auto keeps it off the header slot. */}
            <DeployUpdatePill />
          </header>
          <main className="flex-1 p-3 sm:p-6 lg:p-8">
            {children}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
